import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import { useAuthContext } from '../../../contexts/AuthContext';
import { GoogleCalendarService } from './GoogleCalendarService';
import { SettingsRow, SettingsSection } from '../estabelecimento/Form/SettingsGroup';

const service = new GoogleCalendarService();
export const CONNECT_FLAG = 'googleCalendarConnect';

type StatusResponse = {
    connected: boolean;
    calendarId?: string;
    calendarName?: string;
    accountEmail?: string;
    accountName?: string;
};

export const GoogleCalendarIntegration = () => {
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { user, loadingAuth } = useAuthContext();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<StatusResponse>({
        connected: false,
    });
    const hasProcessedCallback = useRef(false);

    const loadStatus = async () => {
        try {
            const { data } = await service.getStatus();
            setStatus({
                connected: !!data?.connected,
                calendarId: data?.calendarId || 'primary',
                calendarName: data?.calendarName,
                accountEmail: data?.accountEmail,
                accountName: data?.accountName,
            });
        } catch (error) {
            console.error('Erro ao carregar status do Google Calendar:', error);
        }
    };

    useEffect(() => {
        if (!user?.uid) {
            return;
        }
        loadStatus();
    }, [user?.uid]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const error = params.get('error');
        const isGoogleCallback = params.get('googleCalendar') === '1';
        const shouldHandle =
            isGoogleCallback || sessionStorage.getItem(CONNECT_FLAG) === '1';

        if (hasProcessedCallback.current || !shouldHandle) {
            return;
        }

        if (loadingAuth || !user?.uid) {
            return;
        }

        if (error) {
            sessionStorage.removeItem(CONNECT_FLAG);
            errorSnackbar('Não foi possível conectar ao Google Calendar.');
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }

        if (code) {
            hasProcessedCallback.current = true;
            setLoading(true);
            service
                .connect(code)
                .then(() => {
                    successSnackbar('Google Calendar conectado com sucesso!');
                    loadStatus();
                })
                .catch(() => {
                    errorSnackbar('Erro ao conectar o Google Calendar.');
                })
                .finally(() => {
                    sessionStorage.removeItem(CONNECT_FLAG);
                    setLoading(false);
                    window.history.replaceState({}, document.title, window.location.pathname);
                });
        }
    }, [errorSnackbar, loadingAuth, successSnackbar, user?.uid]);

    const handleConnect = async () => {
        try {
            setLoading(true);
            const { data } = await service.getAuthUrl();
            const authUrl = data?.url || data?.authUrl;

            if (!authUrl) {
                throw new Error('authUrl_not_found');
            }

            sessionStorage.setItem(CONNECT_FLAG, '1');
            window.location.href = authUrl;
        } catch (error) {
            console.error('Erro ao iniciar conexão com Google Calendar:', error);
            errorSnackbar('Não foi possível iniciar a conexão.');
            setLoading(false);
        }
    };

    const handleDisconnect = async () => {
        const confirm = window.confirm('Deseja desconectar o Google Calendar?');
        if (!confirm) return;

        try {
            setLoading(true);
            await service.disconnect();
            successSnackbar('Google Calendar desconectado.');
            await loadStatus();
        } catch (error) {
            console.error('Erro ao desconectar Google Calendar:', error);
            errorSnackbar('Não foi possível desconectar.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
            <SettingsSection
                title="Sincronização"
                footer="A sincronização é unidirecional: criar, editar ou cancelar no app atualiza o Google Calendar. Alterações feitas no Google Calendar não alteram o app."
            >
                <SettingsRow label="Status">
                    <span className="estab-row-value">{status.connected ? 'Conectado' : 'Desconectado'}</span>
                </SettingsRow>
                {status.connected && !!status.calendarName && (
                    <SettingsRow label="Calendário">
                        <span className="estab-row-value">{status.calendarName}</span>
                    </SettingsRow>
                )}
                {status.connected && !!status.accountEmail && (
                    <SettingsRow label="Conta Google">
                        <span className="estab-row-value">{status.accountEmail}</span>
                    </SettingsRow>
                )}
                <SettingsRow inline>
                    {status.connected ? (
                        <button
                            type="button"
                            className="estab-link-button estab-link-button--destructive justify-self-start"
                            onClick={handleDisconnect}
                            disabled={loading}
                        >
                            Desconectar
                        </button>
                    ) : (
                        <button
                            type="button"
                            className="estab-link-button justify-self-start"
                            onClick={handleConnect}
                            disabled={loading}
                        >
                            Conectar
                        </button>
                    )}
                </SettingsRow>
            </SettingsSection>
        </div>
    );
};
